import { type PropsWithChildren, useCallback } from 'react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { columnColorSchemes, defaultTheme } from '../constants';
import { useColumn } from './column-context';

export function ColumnThemeProvider({ children }: PropsWithChildren) {
  const {
    primaryColor: settingsPrimaryColor,
    secondaryColor,

    columnStyle,

    button: {
      color: settingsButtonBackgroundColor,
      fontColor: settingsButtonColor
    },

    titleTextColor: settingsTitleTextColor,

    featuresAlign: settingsFeaturesAlign,
    featuresIconColor: settingsFeaturesIconColor,
    featuresTextColor: settingsFeaturesTextColor,

    headBackgroundColor: settingsHeadBackgroundColor,

    highlightBackgroundColor: settingsHighlightBackgroundColor,
    highlightTextColor: settingsHighlightTextColor
  } = useSettings();
  const { isHeadColumn, primaryColor: columnPrimaryColor } = useColumn();
  const hasCustomPrimaryColor = columnPrimaryColor !== 'transparent';

  const getColor = useCallback(
    (color: string, settingsColor?: string) => {
      const colors = {
        primary: hasCustomPrimaryColor
          ? columnPrimaryColor
          : settingsPrimaryColor,
        secondary: settingsColor ?? secondaryColor
      } as const;

      if (color === 'primary' || color === 'secondary') {
        return colors[color];
      }

      return color;
    },
    [
      columnPrimaryColor,
      settingsPrimaryColor,
      secondaryColor,
      hasCustomPrimaryColor
    ]
  );

  const {
    button: { backgroundColor: buttonColor, color: buttonTextColor },
    column: {
      backgroundColor: columnBackgroundColor,
      borderColor: columnBorderColor
    },
    features: { iconColor: featureIconColor },
    highlight: { backgroundColor: highlightColor, color: highlightTextColor },
    title: { backgroundColor: titleColor }
  } = columnColorSchemes[columnStyle];
  const theme = useCallback(
    (outerTheme: DefaultTheme | undefined) => {
      const {
        button,
        column,
        features,
        headColumn = {},
        picture = {},
        price = {},
        title
      } = outerTheme ?? {};
      const {
        color: headColor,
        featuresFont: headFeaturesFont,
        titleFont: headTitleFont
      } = headColumn ?? {};
      const { alignment, font: titleFont } = title ?? {};
      const { font: featuresFont } = features ?? {};

      const titleBackgroundColor = getColor(titleColor);
      const headTitleBackgroundColor =
        titleBackgroundColor === 'transparent'
          ? 'transparent'
          : settingsHeadBackgroundColor;

      return {
        ...defaultTheme,
        picture,
        headColumn,
        price,
        column: {
          ...column,
          backgroundColor: isHeadColumn
            ? settingsHeadBackgroundColor
            : getColor(columnBackgroundColor),
          borderColor: getColor(columnBorderColor)
        },
        features: {
          ...features,
          font: isHeadColumn ? headFeaturesFont : featuresFont,
          iconColor: hasCustomPrimaryColor
            ? getColor(featureIconColor, settingsFeaturesIconColor)
            : settingsFeaturesIconColor,
          color: isHeadColumn ? headColor : settingsFeaturesTextColor,
          align: isHeadColumn ? 'left' : settingsFeaturesAlign
        },
        title: {
          ...title,
          font: isHeadColumn ? headTitleFont : titleFont,
          backgroundColor: isHeadColumn
            ? headTitleBackgroundColor
            : titleBackgroundColor,
          alignment: isHeadColumn ? 'center' : alignment,
          color: isHeadColumn ? headColor : settingsTitleTextColor
        },
        button: {
          ...button,
          color: hasCustomPrimaryColor
            ? getColor(buttonColor, settingsButtonBackgroundColor)
            : settingsButtonBackgroundColor,
          fontColor: hasCustomPrimaryColor
            ? getColor(buttonTextColor, settingsButtonColor)
            : settingsButtonColor
        },
        highlight: {
          backgroundColor: hasCustomPrimaryColor
            ? getColor(highlightColor, settingsHighlightBackgroundColor)
            : settingsHighlightBackgroundColor,
          color: hasCustomPrimaryColor
            ? getColor(highlightTextColor, settingsHighlightTextColor)
            : settingsHighlightTextColor
        }
      };
    },
    [
      isHeadColumn,
      settingsHeadBackgroundColor,
      getColor,
      columnBackgroundColor,
      columnBorderColor,
      hasCustomPrimaryColor,
      featureIconColor,
      settingsFeaturesIconColor,
      settingsFeaturesTextColor,
      settingsFeaturesAlign,
      titleColor,
      settingsTitleTextColor,
      buttonColor,
      settingsButtonBackgroundColor,
      buttonTextColor,
      settingsButtonColor,
      highlightColor,
      settingsHighlightBackgroundColor,
      highlightTextColor,
      settingsHighlightTextColor
    ]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
